import React, { useState } from 'react';
import './InvalidPostingKeyModal.css';
import { userApi } from '../../services/api';
import hiveService from '../../services/hive';

/**
 * InvalidPostingKeyModal
 *
 * Displayed when a `hive:posting-key-invalid` event is fired — e.g. when a
 * broadcast operation fails with an auth error, or when a worker detects that
 * the stored key no longer matches the account's active posting key.
 *
 * Props:
 *   username   {string}  – The affected Hive username
 *   onUpdated  {func}    – Called after the key is successfully verified & saved
 *   onDismiss  {func}    – Called when the user closes without updating
 */
function InvalidPostingKeyModal({ username, onUpdated, onDismiss }) {
  const [newKey, setNewKey] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('enter'); // 'enter' | 'success'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) {
      setError('Please enter your posting key.');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      // 1. Verify the key against the blockchain
      await hiveService.verifyPostingKey(username, newKey.trim());

      // 2. Update the in-memory hive service session
      hiveService.setLoginMethod('postingkey', newKey.trim());
      sessionStorage.setItem('hive_decrypted_posting_key', newKey.trim());

      // 3. Update the server-side stored key and clear the invalid flag
      //    (best-effort – the user may not have a stored key, that's fine)
      try {
        await userApi.storePostingKey(username, newKey.trim());
      } catch (apiErr) {
        // Non-fatal: the key is valid and set in memory; backend update is best-effort
        console.warn('[InvalidPostingKeyModal] Could not update stored key on backend:', apiErr.message);
      }

      setStep('success');
      if (onUpdated) onUpdated(newKey.trim());
    } catch (err) {
      setError(err.message || 'Could not verify the posting key. Please check it and try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="ipkm-overlay" role="dialog" aria-modal="true" aria-labelledby="ipkm-title">
      <div className="ipkm-dialog">
        {/* Header */}
        <div className="ipkm-header">
          <span className="ipkm-icon">⚠️</span>
          <h2 id="ipkm-title" className="ipkm-title">Posting Key Changed</h2>
          <button
            className="ipkm-close"
            aria-label="Dismiss"
            onClick={onDismiss}
            type="button"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="ipkm-body">
          {step === 'enter' ? (
            <>
              <p className="ipkm-description">
                Your Hive posting key for <strong>@{username}</strong> appears to have
                changed or is no longer valid. Until you provide the new key, actions
                that require signing (voting, posting, commenting) will not work.
              </p>
              <p className="ipkm-tip">
                You can find your posting key in the Hive account authority settings
                (e.g. PeakD → Settings → Keys &amp; Permissions).
              </p>

              <form onSubmit={handleSubmit} className="ipkm-form">
                <label htmlFor="ipkm-key-input" className="ipkm-label">
                  New Posting Key
                </label>
                <input
                  id="ipkm-key-input"
                  type="password"
                  className="ipkm-input"
                  value={newKey}
                  onChange={(e) => { setNewKey(e.target.value); setError(null); }}
                  placeholder="5K… (posting key)"
                  autoComplete="off"
                  autoFocus
                  disabled={verifying}
                />

                {error && (
                  <p className="ipkm-error" role="alert">{error}</p>
                )}

                <div className="ipkm-actions">
                  <button
                    type="submit"
                    className="ipkm-btn ipkm-btn-primary"
                    disabled={verifying || !newKey.trim()}
                  >
                    {verifying ? 'Verifying…' : 'Verify & Update'}
                  </button>
                  <button
                    type="button"
                    className="ipkm-btn ipkm-btn-ghost"
                    onClick={onDismiss}
                    disabled={verifying}
                  >
                    Continue without updating
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="ipkm-success">
              <span className="ipkm-success-icon">✅</span>
              <p>Your posting key has been updated and verified successfully.</p>
              <button
                type="button"
                className="ipkm-btn ipkm-btn-primary"
                onClick={onDismiss}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvalidPostingKeyModal;
